import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Copy, RefreshCw, Square } from 'lucide-react';
import { Button, Dialog, DialogFooter, DialogHeader, SegmentedControl } from '../../../components/ui';
import { AiNotice, StreamProgress, useCopy } from '../../intelligence/components/BriefPanel';
import { IntelText } from '../../intelligence/components/IntelText';
import { useIntelligenceStream } from '../../intelligence/useIntelligenceStream';
import type { DigestRequest, SubjectRef } from '../../../types/monitoring';
import { streamDigest } from '../monitoringApi';
import { subjectHref } from '../format';

export type DigestDays = '7' | '14' | '30';

export interface DigestDialogProps {
  open: boolean;
  onClose: () => void;
  days: DigestDays;
  onDaysChange: (days: DigestDays) => void;
  /** Limits the brief to one entity, e.g. from a detail page */
  subject?: SubjectRef | null;
  /** Turns an entity id in the brief into something to open; chips are inert without it */
  resolveSubject?: (id: string) => SubjectRef | undefined;
}

/**
 * "What changed": the digest streamed over SSE and rendered as markdown, using the
 * same stream hook and renderer as the intelligence briefs.
 */
export const DigestDialog: React.FC<DigestDialogProps> = ({
  open,
  onClose,
  days,
  onDaysChange,
  subject,
  resolveSubject,
}) => {
  const navigate = useNavigate();
  const stream = useIntelligenceStream<DigestRequest>(streamDigest);
  const { text, status, error, progress, start, stop, reset } = stream;
  const { copied, copy } = useCopy();
  const running = status === 'running';

  // One run per opening and per window length; closing clears it so it never reads as stale
  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    start({
      days: Number(days),
      ...(subject ? { subjectType: subject.type, subjectId: subject.id } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, days, subject?.id]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      labelledBy="digest-title"
      handle
      className="h-[92dvh] sm:h-[80vh] sm:max-w-2xl"
    >
      <DigestHeader subject={subject} onClose={onClose} />

      <div className="flex flex-shrink-0 items-center gap-2 px-5 pb-3 pt-3">
        <SegmentedControl<DigestDays>
          label="Window"
          value={days}
          onChange={onDaysChange}
          items={[
            { value: '7', label: '7 days' },
            { value: '14', label: '14 days' },
            { value: '30', label: '30 days' },
          ]}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain border-t border-neutral-200/70 px-5 py-4">
        {(status === 'error' || status === 'unavailable') && (
          <AiNotice
            status={status}
            error={error}
            onRetry={() => start({ days: Number(days), ...(subject ? { subjectType: subject.type, subjectId: subject.id } : {}) })}
          />
        )}
        {running && !text && <StreamProgress lines={progress} running className="mb-3" />}
        {text ? (
          <IntelText
            text={text}
            streaming={running}
            onEntity={(id) => {
              const found = resolveSubject?.(id);
              if (!found) return;
              onClose();
              navigate(subjectHref(found));
            }}
          />
        ) : (
          !running &&
          status !== 'error' &&
          status !== 'unavailable' && <p className="text-[13px] text-neutral-400">Nothing to report for this window.</p>
        )}
      </div>

      <DialogFooter>
        {running ? (
          <Button variant="secondary" onClick={stop}>
            <Square className="h-3.5 w-3.5" strokeWidth={1.75} />
            Stop
          </Button>
        ) : (
          <>
            {text && (
              <Button variant="ghost" onClick={() => copy(text)}>
                {copied ? <Check className="h-4 w-4" strokeWidth={2} /> : <Copy className="h-4 w-4" strokeWidth={1.75} />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() =>
                start({ days: Number(days), ...(subject ? { subjectType: subject.type, subjectId: subject.id } : {}) })
              }
            >
              <RefreshCw className="h-4 w-4" strokeWidth={1.75} />
              Again
            </Button>
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </>
        )}
      </DialogFooter>
    </Dialog>
  );
};

const DigestHeader: React.FC<{ subject?: SubjectRef | null; onClose: () => void }> = ({ subject, onClose }) => (
  <DialogHeader
    id="digest-title"
    eyebrow={subject ? subject.label : 'Monitoring'}
    title="What changed"
    onClose={onClose}
  />
);
